import { useQuery } from '@vrplatform/data';
import { toTitleCase } from '@vrplatform/utils';
import { Box, HoverCard, SimpleGrid, Skeleton, Text } from '@mantine/core';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';

type FinancialsRow = Reservation['financials'][0];

function uesFinancialTextAccountQuery(account: FinancialsRow['account']) {
  return useQuery(
    (q, args) => {
      if (!args.accountId)
        return {
          account: null,
        };

      return {
        account:
          q
            .accounts({
              where: { id: { _eq: args.accountId } },
              limit: 1,
            })
            .map((account) => ({
              id: account.id,
              name: account.title || '-',
              category: account.category?.name,
              classification: account.category?.classification,
            }))[0] || null,
      };
    },
    {
      skip: !account?.id,
      variables: {
        accountId: account?.id,
      },
    }
  );
}

export const ReservationFinancialAccountTooltip = ({
  account: initialAccount,
  children,
  offset,
}: {
  account: FinancialsRow['account'];
  children: React.ReactNode;
  offset?: number;
}) => {
  const { data, isLoading } = uesFinancialTextAccountQuery(initialAccount);
  const account = data?.account;

  const labelProps = {
    size: 'xs' as const,
    c: '#737373',
  };

  const valueProps = {
    size: 'xs' as const,
    c: '#262626',
    fw: 500,
  };

  return (
    <HoverCard
      disabled={!initialAccount?.id || !account}
      position="left"
      offset={offset}
    >
      <HoverCard.Target>
        <Box>{children}</Box>
      </HoverCard.Target>
      <HoverCard.Dropdown p={8} maw={280}>
        <SimpleGrid
          cols={2}
          spacing={6}
          sx={{
            gridTemplateColumns: '100px 1fr',
          }}
        >
          <Text {...labelProps}>Account</Text>
          <Text {...valueProps}>
            {isLoading ? (
              <Skeleton height={10} mt={5} width={100} />
            ) : (
              account?.name || '-'
            )}
          </Text>
          <Text {...labelProps}>Classification</Text>
          <Text {...valueProps}>
            {isLoading ? (
              <Skeleton height={10} mt={5} width={100} />
            ) : (
              toTitleCase(account?.classification) || '-'
            )}
          </Text>
          <Text {...labelProps}>Category</Text>
          <Text {...valueProps}>
            {isLoading ? (
              <Skeleton height={10} mt={5} width={100} />
            ) : (
              account?.category || '-'
            )}
          </Text>
        </SimpleGrid>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
